import React, { useContext, useEffect, useState } from 'react';
import './GraphContainer.style.css';

import {
	DiscreteColorLegend,
	VerticalGridLines,
	XAxis,
	YAxis,
	VerticalBarSeries,
	MarkSeries,
	FlexibleXYPlot,
	Hint,
	Crosshair,
	LineSeries,
	LineMarkSeries,
} from 'react-vis';
import CircularProgress from '@material-ui/core/CircularProgress';
import Close from '@material-ui/icons/Close';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Dialog,
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';

import RangeSlider from '../RangeSlider/RangeSlider.component';

import { FilterContext } from '../../contexts/FilterContext';
import {
	display,
	handleCumulativeGraph,
	handleGraphDataRange,
	handleGraphDataSmoothing,
	handleObjectType,
	tooltipStyles,
} from './GraphContainer.utils';
import { decodeStateToObject, encodeStateToArray } from '../../utils';

export interface GraphContainerProps {
	isFilterContainerHidden?: boolean;
	graphData: any;
	rawFrequencyData: any;
	extendedFrequencyData:
		| { x: string; y: number; color: string }[]
		| undefined;
	galaxyData: any;
	colors: string[];
	isError: boolean;
	setIsError: any;
	isPending: boolean;
}

const GraphContainer: React.FC<GraphContainerProps> = ({
	isFilterContainerHidden,
	children,
	graphData,
	rawFrequencyData,
	galaxyData,
	colors,
	extendedFrequencyData,
	setIsError,
	isPending,
}) => {
	const [
		{
			yearSpan,
			freq,
			smoothing,
			graphViewmode,
			searchTerms,
			isCumulative,
			corpus,
			isEmbedded,
		},
	] = useContext(FilterContext);

	const [nearestXY, setNearestXY] = useState<any>({});

	const [isModalShown, setIsModalShown] = useState(false);

	const [filteredGraphData, setFilteredGraphData] = useState<any[]>([]);
	const [galaxyGraphData, setGalaxyGraphData] = useState({
		nodes: [],
		links: [],
	});

	const [isMobile, setIsMobile] = useState(false);

	const [selectedGraph, setSelectedGraph] = useState<
		{ title: string; color: string; index: number }[]
	>([]);
	const [selectedIndexArray, setSelectedIndexArray] = useState<number[]>([]);

	const [isExtendedModalShown, setIsExtendedModalShown] = useState(false);
	const [extendedModalValue, setExtendedModalValue] = useState<any>({});

	useEffect(() => {
		setFilteredGraphData([]);
		setFilteredGraphData(
			handleGraphDataSmoothing(
				handleGraphDataRange(graphData, yearSpan),
				smoothing,
				freq,
			),
		);
		setSelectedGraph([]);
	}, [smoothing, graphData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(
		() =>
			window.innerWidth < 600 ? setIsMobile(true) : setIsMobile(false),
		[],
	);

	useEffect(() => {
		setFilteredGraphData([]);
		setFilteredGraphData(
			handleGraphDataSmoothing(
				handleGraphDataRange(graphData, yearSpan),
				smoothing,
				freq,
			),
		);
	}, [yearSpan]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setSelectedIndexArray(selectedGraph.map((value) => value.index));
	}, [selectedGraph]);

	useEffect(() => {
		const newGraph = { nodes: galaxyData.nodes, links: galaxyData.links };
		setGalaxyGraphData(newGraph);
	}, [galaxyData]);

	useEffect(() => {
		if (galaxyGraphData?.nodes?.length) {
			display(galaxyGraphData, isFilterContainerHidden, colors);
		}
	}, [galaxyGraphData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setFilteredGraphData([]);

		if (isCumulative) {
			setFilteredGraphData(
				handleGraphDataRange(
					handleCumulativeGraph(graphData),
					yearSpan,
				),
			);
		} else {
			setFilteredGraphData(
				handleGraphDataSmoothing(
					handleGraphDataRange(graphData, yearSpan),
					smoothing,
					freq,
				),
			);
		}
	}, [isCumulative, graphData, yearSpan]); // eslint-disable-line react-hooks/exhaustive-deps

	const tableOptions = {
		filter: false,
		downloadOptions: {
			filename: `${searchTerms}-rådata`,
		},
		viewColumns: false,
		selectableRowsHideCheckboxes: true,
		textLabels: {
			body: {
				noMatch:
					'Beklager, fant ingen resultater som samsvarte med ditt søk',
				toolTip: 'Sort',
			},
			pagination: {
				next: 'Neste side',
				previous: 'Forrige side',
				rowsPerPage: 'Resultater per side:',
				displayRows: 'av',
			},
			toolbar: {
				search: 'Søk',
				downloadCsv: 'Last ned rådata i CSV-format',
				print: 'Print',
			},
		},
	};

	return (
		<div
			className={`graph-container ${
				isFilterContainerHidden && 'is-filter-hidden'
			}`}
		>
			{!isEmbedded ? (
				<>
					<div id="graph-wrapper">
						{isPending ? (
							<CircularProgress
								style={{
									position: 'absolute',
									left: '50%',
									transform: 'translateX(-50%)',
								}}
							/>
						) : (
							<>
								{graphViewmode === 'trendlinjer' && (
									<>
										<FlexibleXYPlot
											height={!isMobile ? 560 : 240}
											onMouseEnter={() => {
												setIsModalShown(true);
											}}
											onMouseLeave={() => {
												setIsModalShown(false);
											}}
										>
											<VerticalGridLines
												style={{ opacity: 1 }}
											/>
											{filteredGraphData.map(
												(graph: any, index: number) => (
													<LineSeries
														color={colors[index]}
														key={index + graph?.key}
														data={graph?.values}
														style={{
															strokeLinejoin:
																'round',
															strokeWidth: 3,
															position:
																'relative',
															opacity: `${
																selectedIndexArray.includes(
																	index,
																)
																	? 0.2
																	: 1
															}`,
														}}
													/>
												),
											)}
											{isModalShown && nearestXY && (
												<LineMarkSeries
													color={colors[0]}
													data={[nearestXY]}
													style={{
														strokeLinejoin: 'round',
														strokeWidth: 3,
														position: 'relative',
														cursor: 'pointer',
													}}
												/>
											)}
											<MarkSeries
												data={filteredGraphData
													.map(
														(
															graph: any,
															index: number,
														) => {
															graph.values.forEach(
																(
																	value: any,
																	valueIndex: number,
																) => {
																	value.key =
																		graph.key.toString();
																	if (
																		graphData
																	) {
																		try {
																			value.f =
																				graphData[
																					index
																				].values[
																					valueIndex
																				].f;
																		} catch {
																			setIsError(
																				true,
																			);
																		}
																	}
																},
															);
															return [
																...graph.values,
															];
														},
													)
													.flat()}
												onNearestXY={(e) => {
													try {
														setNearestXY(e);
													} catch {
														setIsError(true);
													}
												}}
												onSeriesClick={() => {
													setExtendedModalValue(
														nearestXY,
													);
													setIsExtendedModalShown(
														true,
													);
												}}
												style={{
													opacity: 0,
													strokeWidth: '4px',
												}}
											/>
											{isModalShown && (
												<Hint
													value={nearestXY}
													style={{ margin: '0.5rem' }}
												>
													<div className="graph-hint">
														<span className="hint-year">
															{nearestXY?.x}
														</span>
														{filteredGraphData.map(
															(
																value: any,
																index: number,
															) => {
																const indexByProp =
																	value.values
																		.map(
																			(
																				e: any,
																			) =>
																				e.x,
																		)
																		.indexOf(
																			nearestXY.x,
																		);
																if (
																	!selectedIndexArray.includes(
																		index,
																	)
																) {
																	return (
																		<span
																			key={
																				value.key
																			}
																			className="custom-hint"
																		>
																			<span className="hint-title-wrapper">
																				{value
																					.values[
																					indexByProp
																				]
																					?.y && (
																					<span>
																						<span
																							className="hint-color"
																							aria-hidden="true"
																							style={{
																								backgroundColor:
																									colors[
																										index
																									],
																							}}
																						></span>
																						<span className="hint-title">
																							{value.key.toString()}
																						</span>
																					</span>
																				)}
																				{graphData[
																					index
																				]
																					.values[
																					indexByProp
																				]
																					?.y && (
																					<span className="hint-percentage">
																						{
																							value
																								.values[
																								indexByProp
																							]
																								?.y
																						}
																						{!isCumulative &&
																							'%'}
																					</span>
																				)}
																			</span>
																			{graphData[
																				index
																			]
																				.values[
																				indexByProp
																			]
																				?.key && (
																				<span
																					className="hint-frequency"
																					style={{
																						display:
																							'block',
																					}}
																				>
																					{graphData[
																						index
																					].values[
																						indexByProp
																					]?.key.toString()}
																				</span>
																			)}
																		</span>
																	);
																}

																return null;
															},
														)}
													</div>
												</Hint>
											)}
											{isExtendedModalShown && (
												<Dialog
													BackdropProps={{
														style: {
															backgroundColor:
																'transparent',
														},
													}}
													aria-labelledby="simple-dialog-title"
													open={isExtendedModalShown}
													onClose={() =>
														setIsExtendedModalShown(
															false,
														)
													}
												>
													<div
														style={{
															padding: '1rem',
														}}
													>
														<h3
															id="simple-dialog-title"
															//@ts-ignore
															style={
																tooltipStyles.title
															}
														>{`${extendedModalValue.key.toString()} - ${extendedModalValue.x.toString()}`}</h3>
														<p
															//@ts-ignore
															style={{
																...tooltipStyles.title,
																fontSize:
																	'13px',
																fontWeight: 400,
															}}
														>
															Prosent:{' '}
															{
																extendedModalValue.y
															}
														</p>

														{extendedModalValue.f && (
															<p
																//@ts-ignore
																style={{
																	...tooltipStyles.title,
																	fontSize:
																		'13px',
																	fontWeight: 400,
																}}
															>
																Frekvens:{' '}
																{
																	// @ts-ignore
																	extendedModalValue.f
																}
															</p>
														)}
														<a
															style={
																tooltipStyles.link
															}
															target="_blank"
															rel="noreferrer"
															href={`https://www.nb.no/search?q=${extendedModalValue.key.toString()}&mediatype=${handleObjectType(
																corpus,
															)}&sort=dateasc&fromDate=${
																extendedModalValue.x
															}0101&toDate=${
																extendedModalValue.x
															}1231`}
															className="tooltip-link"
														>
															Klikk her for å se
															søket ditt i
															NBdigital (
															{extendedModalValue.x.toString()}
															)
														</a>
														<a
															style={
																tooltipStyles.link
															}
															target="_blank"
															rel="noreferrer"
															href={`https://www.nb.no/search?q=${extendedModalValue.key.toString()}&mediatype=${handleObjectType(
																corpus,
															)}&sort=dateasc&fromDate=${
																yearSpan[0]
															}0101&toDate=${
																yearSpan[1]
															}1231`}
															className="tooltip-link"
														>
															Klikk her for å se
															søket ditt i
															NBdigital (
															{`${yearSpan[0]}-${yearSpan[1]}`}
															)
														</a>
														<Button
															style={{
																textTransform:
																	'capitalize',
																marginTop:
																	'1rem',
																color: '#00579e',
															}}
															startIcon={
																<Close />
															}
															onClick={() =>
																setIsExtendedModalShown(
																	false,
																)
															}
														>
															Lukk
														</Button>
													</div>
												</Dialog>
											)}
											{isModalShown && (
												<Crosshair values={[nearestXY]}>
													<div />
												</Crosshair>
											)}
											<MarkSeries
												data={[
													{ x: yearSpan[0], y: 0 },
												]}
												style={{ display: 'none' }}
												color={'000'}
											/>
											<DiscreteColorLegend
												items={[
													...graphData.map(
														(
															graph: any,
															index: number,
														) => {
															return {
																title: graph.key.toString(),
																color: colors[
																	index
																],
															};
														},
													),
												]}
												//@ts-ignore
												onItemClick={(item, index) => {
													const indexByProp =
														selectedGraph
															.map(
																(e: any) =>
																	e.index,
															)
															.indexOf(index);
													if (indexByProp === -1) {
														setSelectedGraph([
															...selectedGraph,
															{ ...item, index },
														]);
													} else {
														const temporaryArray =
															JSON.parse(
																JSON.stringify(
																	selectedGraph,
																),
															);
														temporaryArray.splice(
															indexByProp,
															1,
														);
														setSelectedGraph(
															temporaryArray,
														);
													}
												}}
												orientation="horizontal"
											/>
											<XAxis
												style={{ fontSize: '13px' }}
												tickFormat={function tickFormat(
													d,
												) {
													return d.toString();
												}}
											/>
											<YAxis
												tickLabelAngle={0}
												style={{ fontSize: '13px' }}
											/>
										</FlexibleXYPlot>
									</>
								)}
								{graphViewmode === 'råfrekvens' &&
									(rawFrequencyData.length ? (
										<>
											<FlexibleXYPlot
												height={560}
												xType="ordinal"
												className="raw-frequency-wrapper"
												onMouseEnter={() => {
													setIsModalShown(true);
												}}
												onMouseLeave={() => {
													setIsModalShown(false);
												}}
											>
												<YAxis
													tickLabelAngle={0}
													style={{ fontSize: '13px' }}
												/>
												<VerticalBarSeries
													colorType="literal"
													barWidth={0.6}
													data={rawFrequencyData}
													onNearestXY={(e) =>
														setNearestXY(e)
													}
													style={{
														position: 'relative',
														zIndex: 2,
													}}
												/>
												{isModalShown && (
													<Hint value={nearestXY}>
														<div className="graph-hint">
															<span className="custom-hint">
																<span className="hint-title-wrapper">
																	<span>
																		<span
																			className="hint-color"
																			aria-hidden="true"
																			style={{
																				backgroundColor:
																					nearestXY.color,
																			}}
																		></span>
																		<span className="hint-title">
																			{
																				nearestXY.x
																			}
																		</span>
																	</span>
																	<span className="hint-percentage">
																		{
																			nearestXY.y
																		}
																	</span>
																</span>
															</span>
														</div>
													</Hint>
												)}
												<DiscreteColorLegend
													className="frequency-legend"
													items={[
														...rawFrequencyData.map(
															(
																graph: any,
																index: number,
															) => {
																return {
																	title: graph.x,
																	color: colors[
																		index
																	],
																};
															},
														),
													]}
													orientation="horizontal"
												/>
											</FlexibleXYPlot>
											{extendedFrequencyData &&
												extendedFrequencyData?.length >=
													10 && (
													<MUIDataTable
														title="Utvidet informasjon om grafen"
														// @ts-ignore
														data={
															extendedFrequencyData
														}
														columns={[
															'Ord',
															'Frekvens',
														]}
														options={tableOptions}
													/>
												)}
										</>
									) : (
										<h2 style={{ textAlign: 'center' }}>
											Beklager, fant ingen resultater som
											samsvarte med ditt søk
										</h2>
									))}
								{graphViewmode === 'galakser' && (
									<>
										<div className="graph galaxy-container">
											<div className="force" />
										</div>
										<Accordion
											defaultExpanded={true}
											className="galaxy-information"
										>
											<AccordionSummary
												aria-controls="panel1a-content"
												id="panel1a-header"
											>
												<h3 className="accordion-title">
													Utvidet informasjon om
													grafen
												</h3>
											</AccordionSummary>
											<AccordionDetails>
												<div
													className="galaxydata-html"
													dangerouslySetInnerHTML={{
														__html: galaxyData.html,
													}}
												/>
											</AccordionDetails>
										</Accordion>
									</>
								)}
							</>
						)}
					</div>
					{children}
					{graphViewmode === 'trendlinjer' && <RangeSlider />}
				</>
			) : (
				<div>
					{graphViewmode === 'trendlinjer' && (
						<>
							<FlexibleXYPlot>
								<VerticalGridLines style={{ opacity: 1 }} />
								{filteredGraphData.map(
									(graph: any, index: number) => (
										<LineSeries
											color={colors[index]}
											key={graph?.key}
											data={graph?.values}
											style={{
												strokeLinejoin: 'round',
												strokeWidth: 3,
												position: 'relative',
											}}
										/>
									),
								)}
								<MarkSeries
									data={[{ x: yearSpan[0], y: 0 }]}
									style={{ display: 'none' }}
									color={'000'}
								/>
								<DiscreteColorLegend
									items={[
										...graphData.map(
											(graph: any, index: number) => {
												return {
													title: graph.key.toString(),
													color: colors[index],
												};
											},
										),
									]}
									orientation="horizontal"
								/>
								<XAxis
									style={{ fontSize: '13px' }}
									tickFormat={function tickFormat(d) {
										return d.toString();
									}}
								/>
								<YAxis
									tickLabelAngle={0}
									style={{ fontSize: '13px' }}
								/>
								<div className="embedded-info">
									<span className="embedded-body">
										Kilde: Nasjonalbiblioteket. www.nb.no
									</span>
									<a
										target="_blank"
										rel="noreferrer"
										href={window.location.href.replace(
											window.location.search,
											`?${handleSharingLink(
												window.location.search,
											)}`,
										)}
										className="tooltip-link"
									>
										Se hele grafen og lag ditt eget NB
										N-Gram
									</a>
								</div>
							</FlexibleXYPlot>
						</>
					)}
					{graphViewmode === 'råfrekvens' && (
						<>
							<FlexibleXYPlot xType="ordinal">
								<YAxis
									tickLabelAngle={0}
									style={{ fontSize: '13px' }}
								/>
								<VerticalBarSeries
									colorType="literal"
									barWidth={0.6}
									data={rawFrequencyData}
								/>
								<DiscreteColorLegend
									className="frequency-legend"
									items={[
										...rawFrequencyData.map(
											(graph: any, index: number) => {
												return {
													title: graph.x,
													color: colors[index],
												};
											},
										),
									]}
									orientation="horizontal"
								/>
								<div className="embedded-info">
									<span className="embedded-body">
										Kilde: Nasjonalbiblioteket. www.nb.no
									</span>
									<a
										target="_blank"
										rel="noreferrer"
										href={window.location.href.replace(
											window.location.search,
											`?${handleSharingLink(
												window.location.search,
											)}`,
										)}
										className="tooltip-link"
									>
										Se hele grafen og lag ditt eget NB
										N-Gram
									</a>
								</div>
							</FlexibleXYPlot>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default GraphContainer;

function handleSharingLink(state: string): string {
	const stateObject = decodeStateToObject(state);
	stateObject.isEmbedded = false;
	return encodeStateToArray(stateObject);
}
